import { Button, Checkbox, FormControlLabel, Typography } from "@mui/material";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import GtAlertBanner from "src/components/Common/GtAlertBanner";
import GtCancelSigningDialog from "src/components/Common/GtCancelSigningDialog";
import GtChangeEmailDialog from "src/components/Common/GtChangeEmailDialog";
import GtEngagementLetterDocumentsManagerPanelLinks from "src/components/Common/GtEngagementLetterDocumentsManagerPanelLinks";
import GtEngagementLetterRegenerateConfirmModal from "src/components/Common/GtEngagementLetterRegenerateConfirmModal";
import GtEngagementLetterSignees from "src/components/Common/GtEngagementLetterSignees";
import GtInfoTooltip from "src/components/Common/GtInfoTooltip";
import GtSendNotificationDialog from "src/components/Common/GtSendNotificationDialog";
import GtSetFinalDocumentDialog from "src/components/Common/GtSetFinalDocumentDialog";
import GtSetToManuallySignedDialog from "src/components/Common/GtSetToManuallySignedDialog";
import EngagementLetterDocumentsManagerPanel from "src/components/Panels/EngagementLetterDocumentsManagerPanel";
import { CoreInterfaces } from "src/core/Models";
import { formatDate } from "src/utils";
import * as Constants from "src/core/Constants";
import { useGenerateDocuments } from "./useGenerateDocuments";

function GenerateDocumentsPage(): JSX.Element {
  const {
    t,
    globalState,
    businessUnit,
    language,
    engagementDocuments,
    engagementLetterSignees,
    envelopeIds,
    pageState,
    selectedEngagementLetterSignee,
    isEngagementLetterRegenerateConfirmModalVisible,
    setIsEngagementLetterRegenerateConfirmModalVisible,
    isVisibleSetFinalDocumentDialog,
    setIsVisibleSetFinalDocumentDialog,
    isVisibleSetManuallySignedDialog,
    setIsVisibleSetManuallySignedDialog,
    isVisibleCancelSigningDialog,
    setIsVisibleCancelSigningDialog,
    isVisibleChangeEmailDialog,
    setIsVisibleChangeEmailDialog,
    isVisibleNotifyDialog,
    setIsVisibleNotifyDialog,
    setSelectedEngagementLetterSignee,
    documentPageConfig,
    configurationName,
    createdBy,
    description,
    engagementLetterAndAppendixDocuments,
    engagementDescriptionDocumentArray,
    isEmailMissingDigitalSigningUsers,
    isEmailContainingForbiddenCharactersDigitalSigningUsers,
    handleLanguageChange,
    handleEngagementLetterGenerationFlow,
    handleDocumentAction,
    fetchAndPopulateEngagementDocuments,
    handleOpenFinalDocumentDialogClick,
    startCreateFinalDocumentFlow,
    manuallySignDocument,
    cancelSigningProcess,
    changeEmail,
    sendReminder,
    copyLink,
    downloadSignedDocument,
    sendForDigitalSigning,
    changeConsent,
    generateEngagementDescription,
    isDigitallySigned,
    isObsolete,
    isDigitallySigningEnabled,
  } = useGenerateDocuments();

  return (
    <article className="gt-generateDocumentsPage">
      <section className="gt-generateDocumentsPage__topPanel">
        <Typography
          className="gt-generateDocumentsPage__topPanel-title"
          variant="h5"
          gutterBottom={true}
        >
          {t(pageState.title)}
        </Typography>
      </section>
      <section className="gt-generateDocumentsPage__version">
        <div className="gt-generateDocumentsPage__version-info">
          <div className="gt-generateDocumentsPage__version-info-wrapper">
            <span className="gt-generateDocumentsPage__version-info-wrapper-title">
              {configurationName}
            </span>
            <span className="gt-generateDocumentsPage__version-info-wrapper-createdBy">
              {createdBy}
            </span>
          </div>
          <div className="gt-generateDocumentsPage__version-info-comment">
            {description}
          </div>
        </div>
        {documentPageConfig.isFinalOrOutdatedVersionStatus &&
          globalState.currentConfiguration.signingStatus ===
            Constants.SigningStatus.NotSigned && (
            <div className="gt-generateDocumentsPage__version-ctas">
              <Button
                color="primary"
                variant="contained"
                onClick={() => setIsVisibleSetManuallySignedDialog(true)}
              >
                {t("General.ManuallySetToSigned")}
              </Button>
              {isDigitallySigningEnabled && (
                <Button
                  color="tertiary"
                  variant="contained"
                  disabled={
                    isEmailMissingDigitalSigningUsers ||
                    isEmailContainingForbiddenCharactersDigitalSigningUsers
                  }
                  onClick={() => sendForDigitalSigning()}
                >
                  {t("General.SendForDigitalSigning")}
                </Button>
              )}
            </div>
          )}
      </section>
      <section className="gt-generateDocumentsPage__language">
        <div> {t("General.DocumentsLanguage")}</div>
        <ToggleButtonGroup
          color="primary"
          value={language}
          exclusive={true}
          onChange={handleLanguageChange}
          disabled={documentPageConfig.isOutdatedVersionStatus}
        >
          <ToggleButton value={Constants.Languages.SE}>
            {Constants.Languages.SE}
          </ToggleButton>
          <ToggleButton value={Constants.Languages.EN}>
            {Constants.Languages.EN}
          </ToggleButton>
        </ToggleButtonGroup>
      </section>
      {documentPageConfig.isEngagementLetterRegenerable && (
        <>
          <section className="gt-section">
            <article className="gt-generateDocumentsPanel">
              <section className="gt-generateDocumentsPanel__title">
                <h4>{t("General.EngagementLetter")}</h4>
              </section>
              {documentPageConfig.shouldShowObsoleteEngagementLetterNotification && (
                <section className="gt-generateDocumentsPanel__notification">
                  <GtAlertBanner
                    message={t("General.EngagementLetterObsoleteMessage")}
                    variant={Constants.GtAlertBannerVariant.GenerateDocuments}
                  />
                </section>
              )}
              <section className="gt-generateDocumentsPanel__actions">
                <div className="gt-generateDocumentsPanel__actions-cta">
                  <h5>{t("General.Actions")}</h5>
                  <Button
                    disabled={
                      !documentPageConfig.areAllServicesCompleted ||
                      !globalState.currentConfiguration.id ||
                      documentPageConfig.isFinalOrOutdatedVersionStatus ||
                      (isEmailMissingDigitalSigningUsers &&
                        isDigitallySigningEnabled)
                    }
                    onClick={handleEngagementLetterGenerationFlow}
                    color="tertiary"
                    variant="outlined"
                    key="reGenerateLetterAndAppendixBtn"
                  >
                    {t(documentPageConfig.reGenerateButtonLabel)}
                  </Button>
                </div>
                <div className="gt-generateDocumentsPanel__actions-overview">
                  <h5>{t("General.Overview")}</h5>
                  <div className="gt-generateDocumentsPanel__actions-overview-infoLines">
                    <div className="gt-generateDocumentsPanel__actions-overview-infoLines-line">
                      <span className="gt-generateDocumentsPanel__actions-overview-infoLines-line--verticalAlignMiddle">
                        <i>{t("General.GeneratedBy")}:</i>
                      </span>
                      <span className="gt-generateDocumentsPanel__actions-overview-infoLines-line--personalInfo">
                        {!!globalState.currentConfiguration
                          .engagementLetterMetadata?.createdBy ? (
                          <>
                            <strong>
                              {
                                globalState.currentConfiguration
                                  .engagementLetterMetadata?.createdBy.FirstName
                              }{" "}
                              {
                                globalState.currentConfiguration
                                  .engagementLetterMetadata?.createdBy?.LastName
                              }
                            </strong>
                            <div className="gt-generateDocumentsPanel__actions-overview-infoLines-line-email">
                              {
                                globalState.currentConfiguration
                                  .engagementLetterMetadata?.createdBy?.Email
                              }
                            </div>
                          </>
                        ) : (
                          <>-</>
                        )}
                      </span>
                    </div>
                    <div className="gt-generateDocumentsPanel__actions-overview-infoLines-line">
                      <span className="gt-generateDocumentsPanel__actions-overview-infoLines-line--verticalAlignMiddle">
                        <i>{t("General.DateAndTime")}</i>
                      </span>
                      <span>
                        <strong>
                          {globalState.currentConfiguration
                            .engagementLetterMetadata?.createdDate
                            ? formatDate(
                                globalState.currentConfiguration
                                  .engagementLetterMetadata?.createdDate,
                                Constants.HelpfulConstants
                                  .DateFormatYearMonthDayHourMinutes
                              )
                            : "-"}
                        </strong>
                      </span>
                    </div>
                  </div>
                </div>
                <div className="gt-generateDocumentsPanel__actions-generatedDocuments">
                  <h5>
                    {t("General.GenerateDocuments")}{" "}
                    <GtInfoTooltip
                      tooltipTitle={
                        businessUnit === Constants.BusinessUnit.Audit
                          ? t(
                              "General.TooltipAuditEngagementLetterGenerateDocuments"
                            )
                          : t(
                              "General.TooltipEngagementLetterGenerateDocuments"
                            )
                      }
                      testIdPart="generateDocumentsTooltipTest"
                    />
                  </h5>
                  <GtEngagementLetterDocumentsManagerPanelLinks
                    engagementConfigurationId={
                      globalState.currentConfiguration.id
                    }
                    links={engagementLetterAndAppendixDocuments}
                    hasDownloadLink={documentPageConfig.isFinalVersion}
                    isReadonlyMode={
                      documentPageConfig.isFinalOrOutdatedVersionStatus
                    }
                    clickHandler={handleDocumentAction}
                  />
                </div>
              </section>
              {globalState.currentConfiguration.id &&
                globalState.currentConfiguration.engagementLetterMetadata
                  ?.createdDate && (
                  <section className="gt-generateDocumentsPanel__additionalDocuments">
                    <EngagementLetterDocumentsManagerPanel
                      documents={engagementDocuments.filter(
                        (engagementDocument) =>
                          engagementDocument.Type ===
                          Constants.EngagementDocumentType.AdditionalDocument
                      )}
                      refetchDocuments={() =>
                        fetchAndPopulateEngagementDocuments(true)
                      }
                    />
                  </section>
                )}
            </article>
          </section>
          {isEngagementLetterRegenerateConfirmModalVisible && (
            <GtEngagementLetterRegenerateConfirmModal
              onConfirm={handleEngagementLetterGenerationFlow}
              onDialogClose={() =>
                setIsEngagementLetterRegenerateConfirmModalVisible(false)
              }
            />
          )}
        </>
      )}
      {documentPageConfig.isEngagementDescriptionAreaVisible && (
        <section className="gt-section">
          <article className="gt-generateDocumentsPanel">
            <h4>{t("General.EngagementDescription")}</h4>
            <section className="gt-generateDocumentsPanel__columns">
              <div className="gt-generateDocumentsPanel__columns-left"></div>
              <div className="gt-generateDocumentsPanel__columns-right">
                <h5>
                  {t("General.Actions")}{" "}
                  <GtInfoTooltip
                    tooltipTitle={t(
                      "General.TooltipEngagementDescriptionActions"
                    )}
                    testIdPart="generateDocumentsTooltipTest"
                  />
                </h5>
                <GtEngagementLetterDocumentsManagerPanelLinks
                  engagementConfigurationId={
                    globalState.currentConfiguration.id
                  }
                  links={engagementDescriptionDocumentArray}
                  hasDownloadLink={documentPageConfig.isFinalVersion}
                  isReadonlyMode={
                    documentPageConfig.isFinalVersion ||
                    !globalState.currentConfiguration.id ||
                    documentPageConfig.isOutdatedVersionStatus ||
                    !documentPageConfig.areAllServicesCompleted
                  }
                  clickHandler={
                    !documentPageConfig.isOutdatedVersionStatus &&
                    globalState.currentConfiguration.id &&
                    documentPageConfig.areAllServicesCompleted
                      ? generateEngagementDescription
                      : undefined
                  }
                />
              </div>
            </section>
          </article>
        </section>
      )}

      {!documentPageConfig.isTaxBusinessUnit && (
        <section className="gt-section">
          <article className="gt-generateDocumentsPanel">
            <h4>{t("General.EngagementConfirmation")}</h4>
            <section className="gt-generateDocumentsPanel__columns">
              <div className="gt-generateDocumentsPanel__columns-left">
                <FormControlLabel
                  label={t("General.IsCompetenceInResourcesAvailable")}
                  control={
                    <Checkbox
                      data-testid={"generate-document-checkbox"}
                      size="small"
                      checked={
                        !!globalState.currentConfiguration.consent
                          ?.IsCompetenceInResourcesAvailable
                      }
                      onChange={(e) => {
                        changeConsent({
                          IsCompetenceInResourcesAvailable: e.target.checked,
                        });
                      }}
                      disabled={
                        documentPageConfig.isFinalOrOutdatedVersionStatus
                      }
                    />
                  }
                />
              </div>
            </section>
          </article>
        </section>
      )}
      {(isDigitallySigned || isObsolete) &&
        !!globalState.currentConfiguration.digitalSigningDetails?.Version &&
        !!globalState.currentConfiguration.digitalSigningDetails?.CaseId && (
          <section className="gt-section">
            <article className="gt-generateDocumentsPanel">
              <GtEngagementLetterSignees
                engagementLetterSignees={engagementLetterSignees}
                envelopeIds={envelopeIds}
                cancelSigneeHandler={() => {
                  setIsVisibleCancelSigningDialog(true);
                }}
                downloadDocumentHandler={() => {
                  downloadSignedDocument();
                }}
                isVisibleDownloadBtn={
                  globalState.currentConfiguration.signingStatus ===
                  Constants.SigningStatus.DigitallySigned
                }
                isSigningInProgress={
                  globalState.currentConfiguration.digitalSigningDetails
                    .InProgress
                }
                linkHandler={(
                  engagementLetterSignee: CoreInterfaces.EngagementLetterSignee
                ) => {
                  copyLink(engagementLetterSignee);
                }}
                editHandler={(
                  engagementLetterSignee: CoreInterfaces.EngagementLetterSignee
                ) => {
                  setSelectedEngagementLetterSignee(engagementLetterSignee);
                  setIsVisibleChangeEmailDialog(true);
                }}
                notifyHandler={(
                  engagementLetterSignee: CoreInterfaces.EngagementLetterSignee
                ) => {
                  setSelectedEngagementLetterSignee(engagementLetterSignee);
                  setIsVisibleNotifyDialog(true);
                }}
              />
            </article>
          </section>
        )}

      <section className="gt-navigationButtons">
        {!documentPageConfig.isFinalOrOutdatedVersionStatus &&
          documentPageConfig.isEngagementLetterGenerated && (
            <Button
              data-testid={"set-to-final"}
              color="tertiary"
              variant="contained"
              disabled={
                (!documentPageConfig.isTaxBusinessUnit &&
                  !globalState.currentConfiguration.consent
                    ?.IsCompetenceInResourcesAvailable) ||
                (documentPageConfig.isTaxBusinessUnit &&
                  !globalState.currentConfiguration.engagementLetterMetadata
                    ?.createdDate) ||
                !globalState.currentConfiguration.id ||
                !documentPageConfig.areAllServicesCompleted ||
                ((isEmailMissingDigitalSigningUsers ||
                  isEmailContainingForbiddenCharactersDigitalSigningUsers) &&
                  isDigitallySigningEnabled)
              }
              onClick={() => handleOpenFinalDocumentDialogClick()}
            >
              {t("General.CreateFinalDocuments")}
            </Button>
          )}
      </section>
      {isVisibleSetFinalDocumentDialog && (
        <GtSetFinalDocumentDialog
          onConfirm={() => startCreateFinalDocumentFlow()}
          onDialogClose={() => setIsVisibleSetFinalDocumentDialog(false)}
        />
      )}
      {isVisibleSetManuallySignedDialog && (
        <GtSetToManuallySignedDialog
          onConfirm={(payload: CoreInterfaces.EngagementManuallySigningPack) =>
            manuallySignDocument(payload)
          }
          onDialogClose={() => setIsVisibleSetManuallySignedDialog(false)}
        />
      )}
      {isVisibleCancelSigningDialog && (
        <GtCancelSigningDialog
          onCancelSigning={(comment: string) => {
            cancelSigningProcess(comment);
          }}
          onDialogClose={() => setIsVisibleCancelSigningDialog(false)}
        />
      )}

      {isVisibleChangeEmailDialog && (
        <GtChangeEmailDialog
          engagementLetterSignee={selectedEngagementLetterSignee}
          onEmailChanged={(email) => changeEmail(email)}
          onDialogClose={() => setIsVisibleChangeEmailDialog(false)}
        />
      )}

      {isVisibleNotifyDialog && (
        <GtSendNotificationDialog
          engagementLetterSignee={selectedEngagementLetterSignee}
          onReminderSent={() => {
            sendReminder();
          }}
          onDialogClose={() => setIsVisibleNotifyDialog(false)}
        />
      )}
    </article>
  );
}
export default GenerateDocumentsPage;
